import { supabase } from '../lib/supabase'
import type {
  PaymentSource,
  PaymentSourceInput,
  SoftDeleteParams,
} from '../types/paymentSources'

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

/** Mantém apenas dígitos do CNPJ. */
export function normalizeCnpjDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 14)
}

/** Formata CNPJ como 00.000.000/0000-00 (aceita parcial enquanto digita). */
export function formatCnpjMask(value: string): string {
  const digits = normalizeCnpjDigits(value)
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

export function assertValidCnpj(cnpjDigits: string): void {
  if (!/^\d{14}$/.test(cnpjDigits)) {
    throw new Error('Informe um CNPJ válido com 14 dígitos.')
  }
}

export const paymentSourcesService = {
  async list(includeInactive = true): Promise<PaymentSource[]> {
    let query = supabase
      .from('payment_sources')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as PaymentSource[]
  },

  async create(input: PaymentSourceInput): Promise<PaymentSource> {
    const name = input.name.trim()
    if (!name) throw new Error('Nome é obrigatório.')
    const cnpj = normalizeCnpjDigits(input.cnpj)
    assertValidCnpj(cnpj)

    const { data, error } = await supabase
      .from('payment_sources')
      .insert({
        name,
        cnpj,
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as PaymentSource
  },

  async update(id: string, input: Partial<PaymentSourceInput>): Promise<PaymentSource> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) {
      const name = input.name.trim()
      if (!name) throw new Error('Nome é obrigatório.')
      payload.name = name
    }
    if (input.cnpj !== undefined) {
      const cnpj = normalizeCnpjDigits(input.cnpj)
      assertValidCnpj(cnpj)
      payload.cnpj = cnpj
    }
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('payment_sources')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as PaymentSource
  },

  async softDelete(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error } = await supabase
      .from('payment_sources')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },
}
